import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, Typography } from '@mui/material';
import Keypad from '../Keypad';
import { amountKeyFromEvent, applyAmountKey } from '../amount';
import { formatCents, quickTenders } from '../money';
import { COLORS } from '../theme';

type Props = {
  open: boolean;
  total: number;
  onCancel: () => void;
  /** Called with the tendered amount in cents once the cashier commits. */
  onComplete: (tenderedCents: number) => void;
};

/** Cash tender + change calculator. On-screen keypad *and* hardware
 *  keyboard both work — the counter may or may not be touch.
 *
 *  Amount entry is the same integer-cents accumulator as the custom
 *  amount panel: digits shift left, no decimal point. Two keypads with
 *  different entry models in one till is how a cashier ends up handing
 *  back $1.29 of change instead of $129. */
const CashDialog: React.FC<Props> = ({ open, total, onCancel, onComplete }) => {
  const [tendered, setTendered] = useState(0);

  useEffect(() => {
    if (open) setTendered(0);
  }, [open]);

  const change = tendered - total;
  const canComplete = tendered > 0 && change >= 0;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // Escape belongs to the dialog (cancel), not the accumulator.
      if (e.key === 'Escape') return;
      if (e.key === 'Enter') {
        // Guard on the live value: the listener closes over `tendered`
        // and re-registers each render, so this is current.
        if (tendered > 0 && tendered - total >= 0) {
          e.preventDefault();
          onComplete(tendered);
        }
        return;
      }
      const key = amountKeyFromEvent(e);
      if (key === null) return;
      e.preventDefault();
      setTendered((c) => applyAmountKey(c, key));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: '1.5rem' }}>CASH</Typography>
          <Typography sx={{ color: COLORS.textMuted }}>
            Due{' '}
            <strong style={{ color: COLORS.text, fontSize: '1.25rem' }}>
              {formatCents(total)}
            </strong>
          </Typography>
        </Box>

        <Box
          sx={{
            border: `2px solid ${COLORS.border}`,
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 2,
            textAlign: 'right',
          }}
        >
          <Typography
            sx={{ color: COLORS.textMuted, letterSpacing: '0.1em', fontSize: '0.8rem' }}
          >
            AMOUNT TENDERED
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '2.25rem',
              lineHeight: 1.1,
              fontVariantNumeric: 'tabular-nums',
              color: tendered > 0 ? COLORS.text : COLORS.textMuted,
            }}
            aria-live="polite"
          >
            {formatCents(tendered)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {quickTenders(total).map((amount) => (
            <Button
              key={amount}
              variant="outlined"
              onClick={() => setTendered(amount)}
              sx={{ flex: '1 1 auto', minWidth: 110, fontWeight: 700 }}
            >
              {amount === total ? `${formatCents(amount)} exact` : formatCents(amount)}
            </Button>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'stretch', flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 260px' }}>
            <Keypad
              onKey={(k) => setTendered((c) => applyAmountKey(c, k))}
              keyHeight={64}
            />
          </Box>

          <Box
            sx={{
              flex: '1 1 260px',
              borderRadius: 2,
              border: `2px solid ${canComplete ? COLORS.accent : COLORS.border}`,
              backgroundColor: canComplete ? 'rgba(30,127,79,0.06)' : 'transparent',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Typography sx={{ color: COLORS.textMuted, letterSpacing: '0.1em' }}>
              CHANGE DUE
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '3rem',
                lineHeight: 1.1,
                color: canComplete ? COLORS.accent : COLORS.textMuted,
                fontVariantNumeric: 'tabular-nums',
              }}
              aria-live="polite"
            >
              {canComplete ? formatCents(change) : '—'}
            </Typography>
            {/* Only nag once they've started typing — a fresh dialog
                showing "Short $446.85" is noise, not information. */}
            {tendered > 0 && change < 0 && (
              <Typography sx={{ color: COLORS.warnText, fontWeight: 700, mt: 1 }}>
                Short {formatCents(-change)}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button onClick={onCancel} sx={{ color: COLORS.textMuted, flex: '0 0 140px' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!canComplete}
            onClick={() => onComplete(tendered)}
            sx={{ flex: 1, py: 2, fontSize: '1.2rem', fontWeight: 700 }}
          >
            COMPLETE SALE
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CashDialog;
